// src/components/admin/SeasonManagement.js
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ViewIcon from "@mui/icons-material/RemoveRedEye";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addSeason,
  deleteSeason,
  getSeasons,
  updateSeason,
} from "../../../../store/actions/seasonsActions";

const SeasonManagement = ({ provider }) => {
  const dispatch = useDispatch();
  const seasonsState = useSelector((state) => state.seasons || {});
  const seasonsFromStore = seasonsState.list || [];
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [seasonForm, setSeasonForm] = useState({
    provider: provider?._id || "",
    name: "",
    description: "",
    startDate: new Date(),
    endDate: new Date(),
    venueList: [],
    status: "upcoming",
  });
  const [openView, setOpenView] = useState(false);
  const [newVenue, setNewVenue] = useState("");

  const seasonsPerPage = 5;

  useEffect(() => {
    if (provider?._id) {
      setLoading(true);
      dispatch(getSeasons(provider._id));
    }
  }, [provider, dispatch]);

  useEffect(() => {
    setLoading(false);
  }, [seasonsFromStore]);

  useEffect(() => setPage(1), [search]);

  const filtered = seasonsFromStore.filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (s.name || "").toLowerCase().includes(q) || (s.description || "").toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / seasonsPerPage) || 1;
  const current = filtered.slice((page - 1) * seasonsPerPage, page * seasonsPerPage);

  const openAddDialog = () => {
    setEditingSeason(null);
    setSeasonForm({
      provider: provider?._id || "",
      name: "",
      description: "",
      startDate: new Date(),
      endDate: new Date(),
      venueList: [],
      status: "upcoming",
    });
    setOpenDialog(true);
  };

  const openEditDialog = (season) => {
    setEditingSeason(season);
    setSeasonForm({
      provider: season.provider?._id || season.provider,
      name: season.name,
      description: season.description,
      startDate: season.startDate ? new Date(season.startDate) : new Date(),
      endDate: season.endDate ? new Date(season.endDate) : new Date(),
      venueList: season.venueList || [],
      status: season.status || "upcoming",
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    const payload = { ...seasonForm, provider: provider._id };
    if (editingSeason?._id) {
      await dispatch(updateSeason({ id: editingSeason._id, payload }));
    } else {
      await dispatch(addSeason(payload));
    }
    dispatch(getSeasons(provider._id));
    setOpenDialog(false);
    setEditingSeason(null);
  };

  const handleDelete = (season) => {
    if (!window.confirm(`Delete this season? ${season.name}`)) return;
    dispatch(deleteSeason({ seasonId: season._id, providerId: provider._id }));
  };

  const handleAddVenueToForm = () => {
    if (!newVenue) return;
    if (!seasonForm.venueList.includes(newVenue)) {
      setSeasonForm({ ...seasonForm, venueList: [...seasonForm.venueList, newVenue] });
      setNewVenue("");
    }
  };

  const handleRemoveVenueFromForm = (v) => {
    setSeasonForm({ ...seasonForm, venueList: seasonForm.venueList.filter((item) => item !== v) });
  };

  return (
    <Box sx={{ maxWidth: "99%", mx: "auto" }}>
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search seasons..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} /> }}
          sx={{ flex: 1 }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
          Add Season
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : current.length === 0 ? (
        <Typography>No seasons found</Typography>
      ) : (
        <Stack spacing={2}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Dates</TableCell>
                  <TableCell>Venues</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {current.map((s) => (
                  <TableRow key={s._id}>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>{s.description}</TableCell>
                    <TableCell>
                      {s.startDate ? moment(s.startDate).format("DD-MM-YYYY") : "-"} —{" "}
                      {s.endDate ? moment(s.endDate).format("DD-MM-YYYY") : "-"}
                    </TableCell>
                    <TableCell>
                      {(s.venueList || []).slice(0, 3).map((v) => (
                        <Chip key={v} label={v} size="small" sx={{ mr: 0.5 }} />
                      ))}
                    </TableCell>
                    <TableCell>{s.status}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => setOpenView(s)} color="info"><ViewIcon /></IconButton>
                      <IconButton onClick={() => openEditDialog(s)} color="primary"><EditIcon /></IconButton>
                      <IconButton onClick={() => handleDelete(s)} color="error"><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} />
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingSeason ? "Edit Season" : "Add Season"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField label="Name" value={seasonForm.name} onChange={(e) => setSeasonForm({ ...seasonForm, name: e.target.value })} />
          <TextField label="Description" value={seasonForm.description} onChange={(e) => setSeasonForm({ ...seasonForm, description: e.target.value })} multiline rows={3} />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Start Date" type="date" value={moment(seasonForm.startDate).format("YYYY-MM-DD")} onChange={(e) => setSeasonForm({ ...seasonForm, startDate: e.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField label="End Date" type="date" value={moment(seasonForm.endDate).format("YYYY-MM-DD")} onChange={(e) => setSeasonForm({ ...seasonForm, endDate: e.target.value })} InputLabelProps={{ shrink: true }} />
            <FormControl fullWidth>
              <InputLabel id="season-status-label">Status</InputLabel>
              <Select labelId="season-status-label" value={seasonForm.status} label="Status" onChange={(e) => setSeasonForm({ ...seasonForm, status: e.target.value })}>
                <MenuItem value="upcoming">Upcoming</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle2">Venue List</Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <TextField label="Add Venue" value={newVenue} onChange={(e) => setNewVenue(e.target.value)} sx={{ flex: 1 }} />
              <Button onClick={handleAddVenueToForm}>Add</Button>
            </Box>
            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {seasonForm.venueList.map((v) => (
                <Chip key={v} label={v} onDelete={() => handleRemoveVenueFromForm(v)} sx={{ mr: 0.5 }} />
              ))}
            </Box>
            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>You can also send full venueList array when creating/updating season.</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(openView)} onClose={() => setOpenView(false)} fullWidth maxWidth="sm">
        <DialogTitle>Season Details</DialogTitle>
        <DialogContent>
          {openView && (
            <Box>
              <Typography variant="h6">{openView.name}</Typography>
              <Typography>{openView.description}</Typography>
              <Typography>Dates: {moment(openView.startDate).format("DD-MM-YYYY")} — {moment(openView.endDate).format("DD-MM-YYYY")}</Typography>
              <Box sx={{ mt: 1 }}>{(openView.venueList || []).map((v) => <Chip key={v} label={v} sx={{ mr: 0.5 }} />)}</Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SeasonManagement;
